import React from 'react';
import { CommunityPlanListItem } from '../../types';
import Badge from '../common/Badge';
import BrowseableImage from '../common/BrowseableImage';
import { UnsplashCredit } from '../common/UnsplashCredit';
import './FeaturedPlanCard.css';

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`;

interface FeaturedPlanCardProps {
  plan: CommunityPlanListItem;
  onClick: () => void;
  onCreatorTap?: (name: string, rect: DOMRect) => void;
  onFork?: () => void;
}

const FeaturedPlanCard: React.FC<FeaturedPlanCardProps> = ({ plan, onClick, onCreatorTap, onFork }) => {
  const initials = (plan.creator_name || '?')
    .split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase();
  // Clamped to one line on the card (r1140 finding 1); the title attribute
  // carries the full string. The group is not on the line: a hover-only title
  // was its only disclosure to a keyboard or touch reader, and the plan page
  // shows the days and destination in full but never the group, so the line
  // hides nothing a reader cannot reach (fidelity W4 addition 7).
  const meta = `${plan.duration_days} days · ${plan.destination}`;

  // The credit the CURRENT photograph needs. Browsing swaps the picture, so
  // this follows it rather than being read once from the plan (r774 finding 1).
  const [shown, setShown] = React.useState(plan.hero_attribution ?? null);
  // Whether the image area is the paper fallback, which HeroImage reports,
  // including after a load failure. Bare, there is no scrim and the text
  // over the image goes ink (decision 25, r1123 finding 1).
  const [bare, setBare] = React.useState(!plan.hero_thumb_url);

  return (
    <div className="featured-card-frame">
    <button className={`featured-card${bare ? ' featured-card--bare' : ''}`} onClick={onClick}>
      <div className="featured-card__img">
        <BrowseableImage
          src={plan.hero_thumb_url}
          fallbackKey={plan.title}
          queryKey={plan.photo_query_key}
          size="hero"
          attribution={plan.hero_attribution ?? null}
          onAttributionChange={setShown}
          onBareChange={setBare}
          showLabel={false}
        />
        {!bare && <div className="featured-card__overlay" />}
        {plan.used_count > 0 && (
          <div className="featured-card__fork-count">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 3v6M18 3v6M6 9c0 3 6 3 6 6v3M18 9c0 3-6 3-6 6"/>
              <circle cx="6" cy="3" r="2"/><circle cx="18" cy="3" r="2"/><circle cx="12" cy="21" r="2"/>
            </svg>
            {fmt(plan.used_count)}
          </div>
        )}
      </div>
      {plan.is_staff_pick && (
        <Badge variant="fill" className="featured-card__badge">STAFF PICK</Badge>
      )}
      <div className="featured-card__body">
        <h3 className="featured-card__title">{plan.title}</h3>
        <p className="featured-card__meta" title={meta}>
          {meta}
        </p>
        <div className="featured-card__footer">
          {plan.creator_name && (
            <div
              className="featured-card__creator"
              onClick={(e) => { e.stopPropagation(); onCreatorTap?.(plan.creator_name!, (e.currentTarget as HTMLElement).getBoundingClientRect()); }}
            >
              <span className="featured-card__avatar">{initials}</span>
              <span className="featured-card__creator-name">{plan.creator_name}</span>
            </div>
          )}
          {onFork && (
            <button className="featured-card__fork-btn" onClick={(e) => { e.stopPropagation(); onFork(); }}>
              Make it mine
            </button>
          )}
        </div>
      </div>
    </button>
      {/* OUTSIDE the button, beside the photograph it credits — the pattern
          Explore's cards already ship. An <a> nested in a <button> is invalid
          markup and the photographer link would not be reachable, which is
          the same as not crediting at all. */}
      {shown && (
        <UnsplashCredit
          photographer={shown.photographer}
          profileLink={shown.unsplash_link}
          className="card-photo-credit"
        />
      )}
    </div>
  );
};

export default FeaturedPlanCard;
